/**
 * diffhopService.js
 * Service layer for the DiffHop scaffold-hopping workflow.
 *
 * Conventions (matching the rest of CASCADE):
 *  - Auth:    Authorization: Bearer <id_token from sessionStorage>
 *  - Base:    process.env.REACT_APP_GENCHEM_DEV_API  (swap to PROD via env only)
 *  - Errors:  all functions throw on non-2xx; callers handle display
 */

// const GENCHEM_BASE = process.env.REACT_APP_GENCHEM_DEV_API;
// const DOCKING_BASE = process.env.REACT_APP_DOCKING_DEV_API; // re-used for PDB lookup
const GENCHEM_BASE = 'https://34.160.16.181.nip.io/genchem-api-dev-apigee/';
const DOCKING_BASE = 'https://34.160.16.181.nip.io/docking-api-dev-apigee/api/docking'



// ─── Auth helper ─────────────────────────────────────────────────────────────
function authHeaders() {
  const token = sessionStorage.getItem('id_token');
  return {
    Authorization: `Bearer ${token}`,
  };
}

// ─── Generic request helpers ─────────────────────────────────────────────────
async function requestJson(url, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: {
      ...authHeaders(),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
}

async function fetchTextFromUrl(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch file from ${url}: HTTP ${res.status}`);
  return res.text();
}

// ─── PDB structure lookup (via Docking service — same as DockingWorkspace) ───
/**
 * Resolves a 4-char PDB ID through the backend proxy.
 * Returns: { pdb_id: "4HJO", pdb_file_url: "https://files.rcsb.org/download/4HJO.pdb" }
 */
export async function fetchPdbStructure(pdbId) {
  const id = pdbId.trim().toUpperCase();
  return requestJson(
    `${DOCKING_BASE}/pdb-structure/${encodeURIComponent(id)}`,
    { headers: authHeaders() },
  );
}

/**
 * Full resolution: PDB ID → backend proxy → RCSB file text.
 * Returns: { pdbId, pdbText, pdbFileUrl }
 */
export async function resolvePdbId(pdbId) {
  const info = await fetchPdbStructure(pdbId);
  const pdbText = await fetchTextFromUrl(info.pdb_file_url);
  return {
    pdbId: info.pdb_id || pdbId.toUpperCase(),
    pdbText,
    pdbFileUrl: info.pdb_file_url,
  };
}

// ─── Chain removal ───────────────────────────────────────────────────────────
/**
 * Filters a PDB file string to only keep ATOM/HETATM records for the
 * given chains. All other chain records are stripped.
 * Returns the modified PDB text as a string.
 *
 * @param {string} pdbText   - Raw PDB file content
 * @param {string[]} keepChains - Array of chain IDs to retain e.g. ['A', 'B']
 * @returns {string}
 */
export function filterPdbChains(pdbText, keepChains) {
  if (!keepChains || keepChains.length === 0) return pdbText;
  const keep = new Set(keepChains.map((c) => c.toUpperCase()));
  const lines = pdbText.split('\n');
  const filtered = lines.filter((line) => {
    const rec = line.substring(0, 6).trim().toUpperCase();
    if (rec === 'ATOM' || rec === 'HETATM') {
      // Column 22 (0-indexed: 21) is the chain ID in standard PDB format
      const chainId = line[21]?.toUpperCase() ?? ' ';
      return keep.has(chainId);
    }
    // Always keep non-ATOM/HETATM records (HEADER, REMARK, TER, END, etc.)
    return true;
  });
  return filtered.join('\n');
}

/**
 * Parses all unique chain IDs present in ATOM/HETATM records of a PDB string.
 * Returns a sorted array of chain IDs, e.g. ['A', 'B', 'C']
 *
 * @param {string} pdbText
 * @returns {string[]}
 */
export function parsePdbChains(pdbText) {
  const chains = new Set();
  for (const line of pdbText.split('\n')) {
    const rec = line.substring(0, 6).trim().toUpperCase();
    if (rec === 'ATOM' || rec === 'HETATM') {
      const chainId = line[21]?.trim();
      if (chainId) chains.add(chainId.toUpperCase());
    }
  }
  return Array.from(chains).sort();
}

// ─── Main scaffold-hopping API call ──────────────────────────────────────────
/**
 * Submits a scaffold-hopping job.
 *
 * @param {object} params
 * @param {string}  params.ligandSource  - 'auto_ligand' | 'upload_ligand'
 * @param {string}  [params.pdbId]       - 4-char PDB ID (when ligandSource=auto_ligand)
 * @param {number}  params.nMolecules    - Number of molecules to generate (1–500)
 * @param {string}  params.userId        - From MSAL / location state
 * @param {string}  params.userName      - From MSAL / location state
 * @param {string}  params.projectId     - From location state
 * @param {string}  params.projectName   - From location state
 * @param {File}    [params.proteinFile] - PDB file (required when targetSource='file')
 * @param {File}    [params.ligandFile]  - SDF/MOL file (required when ligandSource='upload_ligand')
 *
 * @returns {Promise<{ taskId: string, status: string, chains_used: string[] }>}
 */
export async function generateScaffolds({
  ligandSource,
  pdbId,
  nMolecules,
  userId,
  userName,
  projectId,
  projectName,
  proteinFile,
  ligandFile,
}) {
  // Build query string
  const params = new URLSearchParams({
    ligandSource,
    nMolecules: String(nMolecules),
    userId: String(userId),
    userName,
    projectId: String(projectId),
    projectName,
  });

  if (pdbId) params.set('pdbId', pdbId.toUpperCase());

  const url = `${GENCHEM_BASE}/api/genchem/diffhopp/generate_scaffolds?${params.toString()}`;

  // Build multipart body (protein_file and ligand_file are optional by combination)
  const formData = new FormData();
  if (proteinFile) formData.append('protein_file', proteinFile);
  if (ligandFile) formData.append('ligand_file', ligandFile);

  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders(), // no Content-Type: browser sets multipart boundary
    body: formData,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }

  return res.json();
  // Expected shape: { taskId: "string_124427", status: "queued", chains_used: ["A"] }
}